import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import useOdEquipmentApi from './config';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';

const Container = styled.div``;

export default function OffboardingEquipmentPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const writable = canWrite ? canWrite('OD') : false;

  const [queryParams, setQueryParams] = useState({ pageSize: 25, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [confirm, setConfirm] = useState(null);

  const { fetching, data, pagination, columns: baseColumns, markReturned, confirm: confirmApi, waive } = useOdEquipmentApi(queryParams);

  const handleDataStateChange = useCallback((act, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => setFilterList(newFilterList), []);

  const buildActions = useCallback((row) => {
    if (!row || !writable) return [];
    const actions = [];
    if (row.status === 'pending') {
      actions.push({
        type: 'edit',
        label: t('offboardingEquipment.action.markReturnedPhysical'),
        onClick: () => setConfirm({
          message: t('offboardingEquipment.confirm.markReturnedPhysical', { item: row.displayName }),
          onYes: () => markReturned(row.id, 'physical'),
        }),
      });
      actions.push({
        type: 'delete',
        label: t('offboardingEquipment.action.waive'),
        onClick: () => setConfirm({
          message: t('offboardingEquipment.confirm.waive', { item: row.displayName }),
          onYes: () => waive(row.id, t('offboardingEquipment.defaultWaiveReason')),
        }),
      });
    }
    if (row.status === 'returned') {
      actions.push({
        type: 'edit',
        label: t('offboardingEquipment.action.confirm'),
        onClick: () => setConfirm({
          message: t('offboardingEquipment.confirm.confirm', { item: row.displayName }),
          onYes: () => confirmApi(row.id),
        }),
      });
    }
    return actions;
  }, [t, writable, markReturned, confirmApi, waive]);

  const columns = useMemo(() => {
    if (!writable) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions', label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            const actions = buildActions(row);
            return actions.length > 0 ? <OptionsMenu actions={actions} row={row} /> : null;
          },
        },
      },
    ];
  }, [baseColumns, data, buildActions, writable]);

  return (
    <Container>
      {useMemo(() => (
        <DataTable
          title={t('offboardingEquipment.pageTitle')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [t, fetching, columns, data, pagination, filterList, handleFilterConfirm, handleDataStateChange])}
      <ConfirmDialog
        isOpen={!!confirm}
        message={confirm?.message}
        onConfirm={async () => { await confirm.onYes(); setConfirm(null); }}
        onCancel={() => setConfirm(null)}
      />
    </Container>
  );
}
